import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from '@mui/material';
import { Calculate, Science, Straighten } from '@mui/icons-material';
import { CalculationType } from '../../../types/DrugDosageCalculator.type';

interface CalculationOption {
  type: CalculationType;
  title: string;
  description: string;
  example: string;
  icon: React.ReactNode;
}

const calculationOptions: CalculationOption[] = [
  {
    type: 'numberOfUnits',
    title: 'Calculate Number of Units',
    description:
      'Calculate how many tablets, ml, or other units to administer based on drug strength and dose.',
    example: 'e.g., "How many ml of 10mg/ml solution for a 2mg/kg dose?"',
    icon: <Calculate />,
  },
  {
    type: 'drugAmount',
    title: 'Calculate Amount of Drug Substance',
    description:
      'Calculate the mg/kg dose when you know the drug strength and number of units given.',
    example: 'e.g., "What mg/kg dose is 2ml of 50mg/ml solution?"',
    icon: <Science />,
  },
  {
    type: 'drugStrength',
    title: 'Calculate Required Strength',
    description:
      'Calculate what drug strength is needed to achieve a specific dose with a given volume.',
    example: 'e.g., "What strength is needed for 1mg/kg dose in 2ml?"',
    icon: <Straighten />,
  },
];

interface CalculationTypeSelectorProps {
  onSelect: (type: CalculationType) => void;
}

export const CalculationTypeSelector: React.FC<
  CalculationTypeSelectorProps
> = ({ onSelect }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography
        sx={{
          fontSize: '1.1em',
          fontWeight: 'bold',
          letterSpacing: '0.1px',
          textTransform: 'uppercase',
          mb: 1,
        }}
      >
        What would you like to calculate?
      </Typography>

      {calculationOptions.map((option) => (
        <Card
          key={option.type}
          variant="outlined"
          sx={{
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CardActionArea onClick={() => onSelect(option.type)}>
            <CardContent
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
            >
              <Box
                sx={{
                  color: 'primary.main',
                  mt: 0.5,
                }}
              >
                {option.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {option.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {option.description}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontStyle: 'italic' }}
                >
                  {option.example}
                </Typography>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Box>
  );
};

export default CalculationTypeSelector;
